import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { SurveyInterface } from 'app/assessment/survey/models';
import { SurveyRestConnectorService } from 'app/assessment/survey/services/survey-rest-connector.service';
import {
  LoadCurrentSurvey,
  LoadCurrentSurveyCompleted,
  LoadSurveys,
  LoadSurveysCompleted,
  ResetCurrentSurvey,
  SurveyActionType,
  UpdateCurrentSurvey,
  UpdateCurrentSurveyCompleted,
  UpdateSurvey,
  UpdateSurveyCompleted,
  UpdateSurveyProgress,
  UpdateSurveyProgressCompleted,
} from 'app/assessment/survey/state/actions';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

@Injectable()
export class SurveyEffects {
  public source: string = 'SurveyEffects';

  /**
   * Load all available surveys.
   */
  @Effect()
  public loadSurveys$: Observable<Action> = this.actions$
    .pipe(
      ofType(SurveyActionType.LoadSurveys),
      map((action: LoadSurveys) => action.payload),
      switchMap((userId: number) => {
        return this._surveyConnector.loadAllByUserId(userId).pipe(
          map((surveys: SurveyInterface[]) => new LoadSurveysCompleted(surveys)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Update the provided survey.
   */
  @Effect()
  public updateSurvey$: Observable<Action> = this.actions$
    .pipe(
      ofType(SurveyActionType.UpdateSurvey),
      map((action: UpdateSurvey) => action.payload),
      switchMap((survey: SurveyInterface) => {
        return this._surveyConnector.update(survey).pipe(
          map((survey: SurveyInterface) => new UpdateSurveyCompleted(survey)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  /**
   * Update survey progress.
   */
  @Effect()
  public updateSurveyProgress$: Observable<Action> = this.actions$
    .pipe(
      ofType(SurveyActionType.UpdateSurveyProgress),
      map((action: UpdateSurveyProgress) => action.payload),
      switchMap((selectedSurveyProgress: number) => {
        return of(selectedSurveyProgress).pipe(
          map((selectedSurveyProgress: number) => new UpdateSurveyProgressCompleted(selectedSurveyProgress)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Load current survey.
   */
  @Effect()
  public loadCurrentSurvey$: Observable<Action> = this.actions$
    .pipe(
      ofType(SurveyActionType.LoadCurrentSurvey),
      map((action: LoadCurrentSurvey) => action.payload),
      switchMap((userId: number) => {// userId as taskID
        return this._surveyConnector.loadCurrent(userId).pipe(
          map((surveys: SurveyInterface) => new LoadCurrentSurveyCompleted(surveys)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));


  /**
   * Update the current survey.
   */
  @Effect()
  public updateCurrentSurvey$: Observable<Action> = this.actions$
    .pipe(
      ofType(SurveyActionType.UpdateCurrentSurvey),
      map((action: UpdateCurrentSurvey) => action.payload),
      switchMap((survey: SurveyInterface) => {
        return this._surveyConnector.update(survey).pipe(
          map((survey: SurveyInterface) => new UpdateCurrentSurveyCompleted(survey)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Reset the current survey.
   */
  @Effect()
  public resetCurrentSurvey$: Observable<Action> = this.actions$
    .pipe(
      ofType(SurveyActionType.ResetCurrentSurvey),
      map((action: ResetCurrentSurvey) => action.payload),
      switchMap((survey: SurveyInterface) => {
        return this._surveyConnector.loadCurrentNew().pipe(
          map((surveys: SurveyInterface) => new LoadCurrentSurveyCompleted(surveys)),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
        // return this._surveyConnector.update(survey).pipe(
        //   map((survey: SurveyInterface) => new ResetCurrentSurveyCompleted(survey)),
        //   catchError((err: any) => of(createErrorRaised(new Error({
        //     message: ErrorMessage.unexpected,
        //     errorResponse: err,
        //     source: this.source,
        //   })))),
        // );
      }));

  constructor(private readonly actions$: Actions, private readonly _surveyConnector: SurveyRestConnectorService) {}

}
